"use client"

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Plus, Edit, Trash } from "lucide-react"
import { useState, useMemo } from "react"
import { Toast } from "../../Toast"
import {
  submitPartnerForm,
  deletePartnerForm,
} from "../../../api/adminService"
import { exportToCSV } from "../../utils/exportToCSV"

const ITEMS_PER_PAGE = 5

export function PartnerFormSection({
  partnerFormList = [],
  setPartnerFormList,
}: any) {
  const [selectedItem, setSelectedItem] = useState<any | null>(null)
  const [formData, setFormData] = useState<any>({})
  const [newItemOpen, setNewItemOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const emptyPartnerForm = {
    full_name: "",
    organization: "",
    email: "",
    phone: "",
    partnership_type: "",
    country: "",
    message: "",
  }

  const [newForm, setNewForm] = useState<any>(emptyPartnerForm)

  const filteredList = useMemo(() => {
    return partnerFormList.filter((item: any) =>
      item.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.currency?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [partnerFormList, searchTerm])

  const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE)
  const paginatedItems = filteredList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleCreate = async () => {
    try {
      const result = await submitPartnerForm(newForm)
      setPartnerFormList((prev: any) => [result, ...prev])
      setNewForm(emptyPartnerForm)
      setNewItemOpen(false)
      Toast.success("Partner form created")
    } catch (err) {
      Toast.error("Create failed")
    }
  }

  const handleUpdate = async () => {
    try {
      const result = await submitPartnerForm(formData)
      setPartnerFormList((prev: any) =>
        prev.map((i: any) => (i.id === result.id ? result : i))
      )
      setSelectedItem(null)
      Toast.success("Partner form updated")
    } catch {
      Toast.error("Update failed")
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deletePartnerForm(id)
      setPartnerFormList((prev: any) => prev.filter((i: any) => i.id !== id))
      Toast.success("Partner form deleted")
    } catch {
      Toast.error("Delete failed")
    }
  }

  const handleDownloadCSV = () => {
    if (partnerFormList.length === 0) {
      Toast.error("No data to export")
      return
    }

    exportToCSV(partnerFormList, "partner-forms.csv")
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center gap-2">
          <div>
            <CardTitle>🤝 Partner Forms</CardTitle>
            <CardDescription>
              Manage partnership requests
            </CardDescription>
          </div>

          <div className="flex gap-2">
            <Button size="sm"  onClick={handleDownloadCSV}>
              Download CSV
            </Button>

            {/* <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" /> New Partner
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Partner</DialogTitle>
                  <DialogDescription>
                    Partnership request details
                  </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3">
                  {Object.keys(newForm).map((key) => (
                    <div key={key} className="col-span-2 sm:col-span-1">
                      <label className="text-sm capitalize">
                        {key.replace("_", " ")}
                      </label>
                      <Input
                        value={newForm[key]}
                        onChange={(e) =>
                          setNewForm({ ...newForm, [key]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setNewItemOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate}>Create</Button>
                </div>
              </DialogContent>
            </Dialog> */}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <Input
          placeholder="Search by name, email, or currency..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {paginatedItems.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            No records found
          </p>
        ) : (
          paginatedItems.map((item: any) => (
            <div key={item.id} className="border rounded p-4">
              <div className="flex justify-between">
                <div>
                  <p className="font-semibold">{item.full_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {item.currency} {item.sponsorship_amount} | {item.email}
                  </p>
                </div>

                {/* <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedItem(item)
                      setFormData(item)
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div> */}
              </div>
            </div>
          )))}


        {/* 📄 Pagination */}
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(currentPage * ITEMS_PER_PAGE, filteredList.length)} of{" "}
            {filteredList.length}
          </p>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              Last
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}